import React, { FC, Fragment, useContext, useEffect, useState } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon } from "@heroicons/react/solid";
import classNames from "../../utils/twClassNames";
import { PickerValue } from "../../types";
import { TranslationContext } from "../../context/TranslationContext";

type Props = {
  values: Array<PickerValue>;
  onChange?: (id: string) => void;
  customHeader?: string;
  currentlySelectedDefaultIndex?: number;
  centerDropDownMenu?: boolean;
  centerDropDownText?: boolean;
};

const SortByDropdown: FC<Props> = ({
  values,
  onChange,
  customHeader,
  currentlySelectedDefaultIndex = 0,
  centerDropDownMenu,
  centerDropDownText,
}) => {
  const { i } = useContext(TranslationContext);
  const [currentlySelected, setcurrentlySelected] = useState(
    values[currentlySelectedDefaultIndex]
  );

  const handleChange = (value: PickerValue) => {
    if (value.id == currentlySelected.id) return;
    if (onChange) {
      onChange(value.id.toString());
    }
    setcurrentlySelected(value);
  };

  return (
    <Listbox value={currentlySelected} onChange={handleChange}>
      {({ open }) => (
        <>
          <div
            className="SortByDropdown flex flex-row w-full items-end h-full"
            data-testid="sort-by-dropdown"
          >
            <Listbox.Button className="border-none bg-transparent flex relative w-full cursor-default focus:outline-none items-center sm:justify-end">
              <span
                className={classNames(
                  centerDropDownText
                    ? "text-center"
                    : "text-left sm:text-right",
                  "block text-custom-black w-full"
                )}
              >
                <span className="font-semibold">
                  {customHeader !== undefined
                    ? customHeader
                    : `${i("Sort by")}: `}
                </span>
                {currentlySelected.name}
              </span>
              <span className="flex items-center pointer-events-none ml-2">
                <img src="/app/assets/downArrowIcon.svg" alt="down arrow" />
              </span>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  className={classNames(
                    centerDropDownMenu ? "-right-[35%]" : "",
                    "text-left absolute z-40 mt-1 w-[250px] bg-white shadow-lg max-h-60 rounded py-1 top-6 text-base focus:outline-none"
                  )}
                >
                  {values.map((value) => (
                    <Listbox.Option
                      data-testid={`sort-by-dropdown-option-${value.name}`}
                      key={value.id}
                      className={({ active }) =>
                        classNames(
                          currentlySelected.name === value.name
                            ? "text-custom-blue bg-custom-light-blue bg-opacity-20"
                            : "text-custom-blue",
                          " hover:bg-custom-light-blue/20 cursor-pointer select-none relative py-2 pl-[22px] pr-[35px] focus:outline-none"
                        )
                      }
                      value={value}
                    >
                      {({ selected, active }) => (
                        <>
                          <span
                            className={classNames(
                              "font-normal",
                              "block truncate"
                            )}
                          >
                            {value.name}
                          </span>
                          {currentlySelected.name === value.name && (
                            <span
                              className={classNames(
                                "text-custom-blue",
                                "absolute inset-y-0 right-0 flex items-center pr-4"
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </Listbox.Button>
          </div>
        </>
      )}
    </Listbox>
  );
};

export default SortByDropdown;
